// Feature-list display text shared by the defense plan editor
// (DefensePlanPanel.web.tsx) and the read-only viewer (DefensePlanViewer.web
// .tsx / DefensePlanViewerCard.web.tsx) via DefensePlanFeatureRow.web.tsx —
// pulled out so the two panels can't drift on what "12,400 sq ft" or
// "0.3 mi" means.
import { featureArea, featureLength } from './planGeometry';
import type { DefensePlanFeature } from '../types';

const METERS_PER_FOOT = 0.3048;
const FEET_PER_MILE = 5280;
const SQFT_PER_ACRE = 43560;
const MILE_THRESHOLD = 0.25;
const ACRE_THRESHOLD = 0.1;

// Pinned to en-US: a US product's units, whatever the browser's locale.
export function formatNumber(value: number, decimals: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatLineLength(meters: number): string {
  const feet = meters / METERS_PER_FOOT;
  const miles = feet / FEET_PER_MILE;
  if (miles >= MILE_THRESHOLD) return `${formatNumber(miles, 1)} mi`;
  return `${formatNumber(feet, 0)} ft`;
}

export function formatAreaSize(sqMeters: number): string {
  const sqFeet = sqMeters / (METERS_PER_FOOT * METERS_PER_FOOT);
  const acres = sqFeet / SQFT_PER_ACRE;
  if (acres < ACRE_THRESHOLD) return `${formatNumber(sqFeet, 0)} sq ft`;
  // Fewer decimals as the number grows: "0.35 acres", "12.4 acres", "11,499 acres".
  const decimals = acres < 10 ? 2 : acres < 100 ? 1 : 0;
  return `${formatNumber(acres, decimals)} acres`;
}

export function formatFeatureMeta(feature: DefensePlanFeature): string {
  // A home feature (kind 'point' + structureId — see
  // DefensePlanFeatureSchema's comment) shows its address instead of the
  // generic "Point" every other point reads as; 'Home' covers the
  // structureId-but-no-address edge case rather than showing nothing.
  if (feature.structureId) return feature.address || 'Home';
  if (feature.kind === 'point') return 'Point';
  if (feature.kind === 'line') return `Line · ${formatLineLength(featureLength(feature.coords))}`;
  return `Area · ${formatAreaSize(featureArea(feature.coords))}`;
}
